import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { posthog } from 'posthog-js'

import { firebaseApp } from './firebase'

/**
 * PostHog project API key. Public by design: it ships inside the browser
 * bundle and can only write events, never read them — exactly like the Sentry
 * DSN hard-coded in ./tracing. The secret counterpart (a personal key, `phx_`)
 * is never needed by the app.
 *
 * From PostHog → Settings → Project → Project API key.
 */
const PROJECT_KEY = 'phc_zmvoBhRqhcxcrmDmVhVwAzKwpH6xS4qtDp3WiwyJDcEo'

/**
 * EU cloud — verified against this key: the EU endpoint accepts it, the US one
 * answers 401. The two must match, because a key sent to the wrong region fails
 * authentication and every event is dropped with nothing to show for it.
 */
const API_HOST = 'https://eu.i.posthog.com'

/**
 * Analytics runs in production only, mirroring ./tracing. Local development
 * would otherwise pollute the funnels we are about to measure, and the numbers
 * that matter here are conversion rates — a handful of developer sessions moves
 * them visibly.
 *
 * The key check keeps a build with an unset key from shipping a broken PostHog
 * client to every user.
 */
const ENABLED = import.meta.env.PROD && PROJECT_KEY.startsWith('phc_')

if (ENABLED) {
	posthog.init(PROJECT_KEY, {
		api_host: API_HOST,
		// Pin the behaviour snapshot rather than inheriting whatever the next
		// posthog-js release decides. An upgrade should never silently change
		// what we measure — a jump in a funnel must mean a jump in the product.
		defaults: '2026-08-30',
		// This is a single-page app: React Router swaps views without a document
		// load, so the default (capture on load only) would record exactly one
		// pageview per session and every in-app screen would be invisible.
		// Listening to history changes covers the router without the analytics
		// having to reach into it — router.tsx stays untouched.
		capture_pageview: 'history_change',
		// Session replay already runs through Sentry at a 10% sample (see
		// ./tracing). Recording again here would double the recorder's cost in
		// the visitor's browser and split the recordings across two products,
		// leaving neither complete. If PostHog's replay turns out to be the more
		// useful one — it links a recording to the funnel step where the user
		// dropped — switch the two over together, never run both.
		disable_session_recording: true,
	})

	// Tie events to the signed-in account, so a funnel can be read per user and
	// not just per browser.
	//
	// `identify` is called only on a real change, and `reset` only on a real
	// sign-out. Firebase fires this listener with `null` on every page load
	// before it restores the session; resetting there would hand the visitor a
	// fresh anonymous id on each load, and every multi-page funnel would look
	// like a crowd of one-step strangers.
	let identified: string | null = null
	onAuthStateChanged(getAuth(firebaseApp), (user) => {
		if (user) {
			if (identified !== user.uid) {
				identified = user.uid
				posthog.identify(user.uid, { email: user.email ?? undefined })
			}
			return
		}
		if (identified) {
			identified = null
			posthog.reset()
		}
	})
}

/**
 * Record a product event.
 *
 * A no-op when analytics is off, so call sites never need to guard. Event names
 * are snake_case and read as something that happened, not as a screen that
 * exists — PostHog builds funnels out of these strings, and renaming one later
 * breaks the history of the funnel it belongs to.
 */
export function track(event: string, properties?: Record<string, unknown>): void {
	if (!ENABLED) {
		return
	}
	posthog.capture(event, properties)
}
